import { api } from '@/services/config/config';
import { Response } from '@/services/config/types';
import { TMessage } from '@/store/chat/types';
import { DEFAULT_PAGINATION } from '@/store/thread/threadSlice';
import { TChatThread, ThreadData, ThreadPagination } from '@/store/thread/types';

import {
  TDeleteResponse,
  TFetchThreadsRequest,
  TFetchThreadsResponse,
  TMessageResponse,
  TThreadDeleteRequest,
  TTransformedDeleteResponse,
  TUpdateThreadPayload,
  TUpdateThreadResponse,
} from './types';

export const threadApi = api.injectEndpoints({
  endpoints: (builder) => ({
    fetchThreads: builder.query<Response<ThreadData>, TFetchThreadsRequest>({
      query: ({ userId, page, limit, orderBy, sortOrder, searchValue, onlyFavorites }) =>
        `/chat/${userId}/threads?page=${page}&limit=${limit}&order_by=${orderBy}&sort_order=${sortOrder}&search_value=${searchValue}&only_favorites=${onlyFavorites}`,
      // INFO: Ensures RTK invalidates its cache immediatly after the component is unmonuted to avoid race conditions when fetching favorites threads <-> all threads
      keepUnusedDataFor: 0,
      transformResponse: (response: Response<TFetchThreadsResponse>) => {
        let threads: TChatThread[] = [];
        let pagination: ThreadPagination = DEFAULT_PAGINATION;

        if (response?.data?.threads) {
          threads = response.data?.threads?.map(
            (thread): TChatThread => ({
              title: thread.title,
              threadId: thread.thread_id,
              timestamp: thread.last_updated,
              isFavorite: thread.favorite,
            }),
          );
        }

        if (response.data?.pagination) {
          pagination = {
            totalThreads: response.data?.pagination?.total_threads,
            totalPages: response.data?.pagination?.total_pages,
            currentPage: response.data?.pagination?.current_page,
            limit: response.data?.pagination?.limit,
            hasNextPage: response.data?.pagination?.has_next_page,
            hasPreviousPage: response.data?.pagination?.has_previous_page,
            sortOrder: response.data?.pagination?.sort_order,
            onlyFavorites: response.data?.pagination?.only_favorites,
          };
        }

        return { ...response, data: { threads, pagination } };
      },
    }),
    deleteThread: builder.mutation<Response, Pick<Required<TThreadDeleteRequest>, 'threadId' | 'userId'>>({
      query: ({ userId, threadId }) => ({
        url: `/chat/${userId}/threads/${threadId}`,
        method: 'DELETE',
      }),
    }),
    deleteThreads: builder.mutation<Response, Pick<TThreadDeleteRequest, 'userId'>>({
      query: ({ userId }) => ({
        url: `/chat/${userId}/threads/`,
        method: 'DELETE',
      }),
    }),
    deleteMessagesByThreadId: builder.query<TTransformedDeleteResponse, TThreadDeleteRequest>({
      query: ({ userId, threadId, messageId }) => ({
        url: `/chat/${userId}/threads/${threadId}/messages/${messageId}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Response<TDeleteResponse>): TTransformedDeleteResponse => {
        const messageIds = [response.data?.ai_message_id ?? '', response.data?.human_message_id ?? ''];
        return { status: response.status, messageIds };
      },
    }),
    fetchMessagesByThreadId: builder.query<TMessage[], Pick<TFetchThreadsRequest, 'threadId' | 'userId'>>({
      query: ({ userId, threadId }) => `/chat/${userId}/messages/${threadId}`,
      transformResponse: (response: Response<TMessageResponse>) => {
        if (!response.data?.messages) return [];
        const { thread_id, messages } = response.data;

        return messages.map(
          (msg): TMessage => ({
            content: msg.content,
            role: msg.role,
            threadId: thread_id,
            messageId: msg.message_id,
            timestamp: msg.last_updated,
            data_visualization: msg.data_visualization,
            follow_ups: msg.follow_ups,
            derivesFromPromptLibrary: msg.prompt_library,
          }),
        );
      },
    }),
    updateThread: builder.mutation<TUpdateThreadResponse, TUpdateThreadPayload>({
      query: ({ userId, threadId, dataToUpdate }) => ({
        url: `/chat/${userId}/threads/${threadId}`,
        method: 'PATCH',
        body: dataToUpdate,
      }),
    }),
  }),
});

export const {
  useFetchThreadsQuery,
  useLazyFetchThreadsQuery,
  useDeleteThreadMutation,
  useDeleteThreadsMutation,
  useUpdateThreadMutation,
} = threadApi;
